import { useState, useMemo } from "react";
import { H1, Body } from "@datapay-by-datacom/design-system";
import { DateRangeToolbar } from "../../analytics/components/DateRangeToolbar/DateRangeToolbar";
import { OverviewCards } from "../../analytics/components/OverviewCards/OverviewCards";
import { ActiveUsersChart } from "../../analytics/components/ActiveUsersChart/ActiveUsersChart";
import { UserGrowthChart } from "../../analytics/components/UserGrowthChart/UserGrowthChart";
import { RetentionCards } from "../../analytics/components/RetentionCards/RetentionCards";
import { ThreadDepthChart } from "../../analytics/components/ThreadDepthChart/ThreadDepthChart";
import { FeedbackChart } from "../../analytics/components/FeedbackChart/FeedbackChart";
import { CompletionsChart } from "../../analytics/components/CompletionsChart/CompletionsChart";
import { CompletionTimeseriesChart } from "../../analytics/components/CompletionTimeseriesChart/CompletionTimeseriesChart";
import { OrgAdoptionChart } from "../components/OrgAdoptionChart/OrgAdoptionChart";
import { DocumentSourcesTable } from "../../analytics/components/DocumentSourcesTable/DocumentSourcesTable";
import { TopicClusterChart } from "../../analytics/components/TopicClusterChart/TopicClusterChart";
import { McpOverviewCards } from "../../analytics/components/McpOverviewCards/McpOverviewCards";
import { McpInteractionTimeseriesChart } from "../../analytics/components/McpInteractionTimeseriesChart/McpInteractionTimeseriesChart";
import { McpVelocityChart } from "../../analytics/components/McpVelocityChart/McpVelocityChart";
import { ChartSkeleton } from "../../analytics/components/ChartSkeleton/ChartSkeleton";
import { CardErrorBoundary } from "../../analytics/components/CardErrorBoundary/CardErrorBoundary";
import { useDateRange } from "../../analytics/hooks/useDateRange";
import { useOverview } from "../hooks/useOverview";
import { useActiveUsers } from "../hooks/useActiveUsers";
import { useUserGrowth } from "../hooks/useUserGrowth";
import { useReturningUsers } from "../hooks/useReturningUsers";
import { useReturningOrgs } from "../hooks/useReturningOrgs";
import { useThreadDepth } from "../hooks/useThreadDepth";
import { useFeedbackSummary } from "../hooks/useFeedbackSummary";
import { useCompletionsPerUser } from "../hooks/useCompletionsPerUser";
import { useCompletionTimeseries } from "../hooks/useCompletionTimeseries";
import { useOrgAdoption } from "../hooks/useOrgAdoption";
import { useDocumentSources } from "../hooks/useDocumentSources";
import { useTopicClusters } from "../hooks/useTopicClusters";
import { useMcpOverview } from "../hooks/useMcpOverview";
import { useMcpInteractionTimeseries } from "../hooks/useMcpInteractionTimeseries";
import { useMcpVelocity } from "../hooks/useMcpVelocity";
import { formatShortDate } from "../utils/formatters";
import type { Period } from "../../generated/core-api";
import styles from "./PlatformAnalyticsDashboard.module.css";

type CardKey =
  | "overview"
  | "activeUsers"
  | "userGrowth"
  | "userRetention"
  | "orgRetention"
  | "threadDepth"
  | "feedbackSummary"
  | "completionTimeseries"
  | "completionsPerUser"
  | "orgAdoption"
  | "documentSources"
  | "topicClusters"
  | "mcpOverview"
  | "mcpInteractionTimeseries"
  | "mcpVelocity";

function cardSubtitle(
  key: CardKey,
  fromDate: string,
  toDate: string,
  period: Period,
): string {
  const start = formatShortDate(fromDate);
  const end = formatShortDate(toDate);
  const periodName = period.charAt(0).toUpperCase() + period.slice(1);

  const subtitles: Record<CardKey, string> = {
    overview: `Platform summary from ${start} to ${end}.`,
    activeUsers: `${periodName} active users from ${start} to ${end}. Active means created a new thread.`,
    userGrowth: `Trend in new user numbers and cumulative user growth from ${start} to ${end}.`,
    userRetention: `User retention metrics from ${start} to ${end}.`,
    orgRetention: `Organisation retention metrics from ${start} to ${end}.`,
    threadDepth: `Distribution of thread message counts from ${start} to ${end}.`,
    feedbackSummary: `Feedback ratings and unrated threads from ${start} to ${end}.`,
    completionTimeseries: `${periodName} completion counts from ${start} to ${end}.`,
    completionsPerUser: `Top users by completion request count from ${start} to ${end}.`,
    orgAdoption: "Member and admin counts per organisation.",
    documentSources: `Most referenced documents by citation count from ${start} to ${end}.`,
    topicClusters: `Thread topic clusters via UMAP dimensionality reduction from ${start} to ${end}.`,
    mcpOverview: `MCP interaction summary from ${start} to ${end}.`,
    mcpInteractionTimeseries: `${periodName} MCP interaction counts from ${start} to ${end}.`,
    mcpVelocity: `MCP interaction velocity (per hour) from ${start} to ${end}.`,
  };

  return subtitles[key];
}

function CardShell({
  title,
  subtitle,
  children,
}: {
  title: string;
  subtitle?: string;
  children?: React.ReactNode;
}) {
  return (
    <div className={styles.card}>
      <h3 className={styles.cardTitle}>{title}</h3>
      {subtitle && <p className={styles.cardSubtitle}>{subtitle}</p>}
      <div className={styles.cardBody}>
        <CardErrorBoundary>
          {children ?? <Body>Coming soon</Body>}
        </CardErrorBoundary>
      </div>
    </div>
  );
}

function ErrorPlaceholder({ message }: { message: string }) {
  return <Body>Error: {message}</Body>;
}

function FilteredTopicClusters({
  data,
  source,
}: {
  data: import("../../generated/core-api").TopicClusterResponse;
  source: "all" | "thread" | "mcp";
}) {
  const filtered = useMemo(() => {
    if (source === "all") return data;
    const threads = data.threads.filter((t) => t.source === source);
    const clusterIds = new Set(threads.map((t) => t.cluster_id));
    return {
      ...data,
      threads,
      cluster_summary: data.cluster_summary.filter((c) =>
        clusterIds.has(c.cluster_id),
      ),
      total_threads: threads.length,
      total_clusters: [...clusterIds].filter((id) => id >= 0).length,
    };
  }, [data, source]);

  return <TopicClusterChart data={filtered} />;
}

export function PlatformAnalyticsDashboard() {
  const dateRange = useDateRange();

  const sub = (key: CardKey) =>
    cardSubtitle(key, dateRange.fromDate, dateRange.toDate, dateRange.period);

  const overview = useOverview(dateRange.fromDate, dateRange.toDate);
  const activeUsers = useActiveUsers(
    dateRange.fromDate,
    dateRange.toDate,
    dateRange.period,
  );
  const userGrowth = useUserGrowth(dateRange.fromDate, dateRange.toDate);
  const returningUsers = useReturningUsers(dateRange.fromDate, dateRange.toDate);
  const returningOrgs = useReturningOrgs(dateRange.fromDate, dateRange.toDate);
  const threadDepth = useThreadDepth(dateRange.fromDate, dateRange.toDate);
  const feedbackSummary = useFeedbackSummary(dateRange.fromDate, dateRange.toDate);
  const completionTimeseries = useCompletionTimeseries(
    dateRange.fromDate,
    dateRange.toDate,
    dateRange.period,
  );
  const completionsPerUser = useCompletionsPerUser(dateRange.fromDate, dateRange.toDate);
  const orgAdoption = useOrgAdoption();
  const documentSources = useDocumentSources(dateRange.fromDate, dateRange.toDate);
  const [topicSource, setTopicSource] = useState<"all" | "thread" | "mcp">("all");
  const topicClusters = useTopicClusters(dateRange.fromDate, dateRange.toDate, true);
  const mcpOverview = useMcpOverview(dateRange.fromDate, dateRange.toDate);
  const mcpInteractionTimeseries = useMcpInteractionTimeseries(
    dateRange.fromDate,
    dateRange.toDate,
    dateRange.period,
  );
  const mcpVelocity = useMcpVelocity(
    dateRange.fromDate,
    dateRange.toDate,
    dateRange.period,
  );

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <H1>Platform Analytics</H1>
        <DateRangeToolbar
          fromDate={dateRange.fromDate}
          toDate={dateRange.toDate}
          period={dateRange.period}
          isDatePickerOpen={dateRange.isDatePickerOpen}
          onDatePickerOpenChange={dateRange.setDatePickerOpen}
          onDateChange={dateRange.setDateRange}
          onPeriodChange={dateRange.setPeriod}
        />
      </div>

      {/* Overview KPI Cards — full width */}
      <section className={styles.fullWidth}>
        <CardShell title="Overview" subtitle={sub("overview")}>
          {overview.isLoading && <ChartSkeleton variant="kpi" />}
          {overview.error && <ErrorPlaceholder message={overview.error.message} />}
          {overview.data && <OverviewCards data={overview.data} />}
        </CardShell>
      </section>

      {/* Paired chart sections */}
      <section className={styles.grid}>
        <CardShell title="Active Users" subtitle={sub("activeUsers")}>
          {activeUsers.isLoading && <ChartSkeleton variant="chart" />}
          {activeUsers.error && <ErrorPlaceholder message={activeUsers.error.message} />}
          {activeUsers.data && <ActiveUsersChart data={activeUsers.data} />}
        </CardShell>
        <CardShell title="User Growth" subtitle={sub("userGrowth")}>
          {userGrowth.isLoading && <ChartSkeleton variant="chart" />}
          {userGrowth.error && <ErrorPlaceholder message={userGrowth.error.message} />}
          {userGrowth.data && <UserGrowthChart data={userGrowth.data} />}
        </CardShell>
      </section>

      <section className={styles.grid}>
        <CardShell title="User Retention" subtitle={sub("userRetention")}>
          {returningUsers.isLoading && <ChartSkeleton variant="kpi" />}
          {returningUsers.error && <ErrorPlaceholder message={returningUsers.error.message} />}
          {returningUsers.data && <RetentionCards variant="users" data={returningUsers.data} />}
        </CardShell>
        <CardShell title="Org Retention" subtitle={sub("orgRetention")}>
          {returningOrgs.isLoading && <ChartSkeleton variant="kpi" />}
          {returningOrgs.error && <ErrorPlaceholder message={returningOrgs.error.message} />}
          {returningOrgs.data && <RetentionCards variant="orgs" data={returningOrgs.data} />}
        </CardShell>
      </section>

      <section className={styles.grid}>
        <CardShell title="Thread Depth" subtitle={sub("threadDepth")}>
          {threadDepth.isLoading && <ChartSkeleton variant="chart" />}
          {threadDepth.error && <ErrorPlaceholder message={threadDepth.error.message} />}
          {threadDepth.data && <ThreadDepthChart data={threadDepth.data} />}
        </CardShell>
        <CardShell title="Feedback Summary" subtitle={sub("feedbackSummary")}>
          {feedbackSummary.isLoading && <ChartSkeleton variant="chart" />}
          {feedbackSummary.error && <ErrorPlaceholder message={feedbackSummary.error.message} />}
          {feedbackSummary.data && <FeedbackChart data={feedbackSummary.data} totalThreads={overview.data?.total_threads} />}
        </CardShell>
      </section>

      {/* Completion Timeseries — full width */}
      <section className={styles.fullWidth}>
        <CardShell title="Completion Timeseries" subtitle={sub("completionTimeseries")}>
          {completionTimeseries.isLoading && <ChartSkeleton variant="chart" />}
          {completionTimeseries.error && <ErrorPlaceholder message={completionTimeseries.error.message} />}
          {completionTimeseries.data && <CompletionTimeseriesChart data={completionTimeseries.data} />}
        </CardShell>
      </section>

      <section className={styles.grid}>
        <CardShell title="Completions per User" subtitle={sub("completionsPerUser")}>
          {completionsPerUser.isLoading && <ChartSkeleton variant="chart" />}
          {completionsPerUser.error && <ErrorPlaceholder message={completionsPerUser.error.message} />}
          {completionsPerUser.data && <CompletionsChart data={completionsPerUser.data} />}
        </CardShell>
        <CardShell title="Org Adoption" subtitle={sub("orgAdoption")}>
          {orgAdoption.isLoading && <ChartSkeleton variant="chart" />}
          {orgAdoption.error && <ErrorPlaceholder message={orgAdoption.error.message} />}
          {orgAdoption.data && <OrgAdoptionChart data={orgAdoption.data} />}
        </CardShell>
      </section>

      {/* Full-width sections */}
      <section className={styles.fullWidth}>
        <CardShell title="Document Sources" subtitle={sub("documentSources")}>
          {documentSources.isLoading && <ChartSkeleton variant="table" />}
          {documentSources.error && <ErrorPlaceholder message={documentSources.error.message} />}
          {documentSources.data && <DocumentSourcesTable data={documentSources.data} />}
        </CardShell>
      </section>

      {/* ── MCP Analytics ── */}
      <section className={styles.fullWidth}>
        <div className={styles.sectionDivider}>
          <h2 className={styles.sectionTitle}>MCP Interactions</h2>
        </div>
      </section>

      <section className={styles.fullWidth}>
        <CardShell title="MCP Overview" subtitle={sub("mcpOverview")}>
          {mcpOverview.isLoading && <ChartSkeleton variant="kpi" />}
          {mcpOverview.error && <ErrorPlaceholder message={mcpOverview.error.message} />}
          {mcpOverview.data && <McpOverviewCards data={mcpOverview.data} />}
        </CardShell>
      </section>

      <section className={styles.fullWidth}>
        <CardShell title="MCP Interaction Timeseries" subtitle={sub("mcpInteractionTimeseries")}>
          {mcpInteractionTimeseries.isLoading && <ChartSkeleton variant="chart" />}
          {mcpInteractionTimeseries.error && <ErrorPlaceholder message={mcpInteractionTimeseries.error.message} />}
          {mcpInteractionTimeseries.data && <McpInteractionTimeseriesChart data={mcpInteractionTimeseries.data} />}
        </CardShell>
      </section>

      <section className={styles.fullWidth}>
        <CardShell title="MCP Velocity" subtitle={sub("mcpVelocity")}>
          {mcpVelocity.isLoading && <ChartSkeleton variant="chart" />}
          {mcpVelocity.error && <ErrorPlaceholder message={mcpVelocity.error.message} />}
          {mcpVelocity.data && <McpVelocityChart data={mcpVelocity.data} />}
        </CardShell>
      </section>

      <section className={styles.fullWidth}>
        <CardShell title="Topic Clusters" subtitle={sub("topicClusters")}>
          <div className={styles.topicControls}>
            <label className={styles.selectLabel}>
              Source
              <select
                className={styles.sourceSelect}
                value={topicSource}
                onChange={(e) => setTopicSource(e.target.value as "all" | "thread" | "mcp")}
              >
                <option value="all">All Interactions</option>
                <option value="thread">PA Interactions</option>
                <option value="mcp">MCP Interactions</option>
              </select>
            </label>
          </div>
          {topicClusters.isLoading && <ChartSkeleton variant="scatter" />}
          {topicClusters.error && <ErrorPlaceholder message={topicClusters.error.message} />}
          {topicClusters.data && <FilteredTopicClusters data={topicClusters.data} source={topicSource} />}
        </CardShell>
      </section>
    </div>
  );
}
